import { Trans, useLingui } from '@lingui/react/macro';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { BaseModal } from '@/components/ui/base-modal';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';

/**
 * AI-description optimisation modal (M7). Shape mirrors M6
 * (`OptimizeNameModal`) but for the longer-form description field —
 * disabled current value, generated suggestion editable in a
 * textarea, Accept/Cancel.
 *
 * `generate` is supplied by the caller so Step 1 can wire to
 * `optimizeDescriptionStep1` (basic context) while Step 4 wires to
 * `optimizeDescriptionStep4` (full template context).
 */
export function OptimizeDescriptionModal({
  currentName,
  currentDescription,
  generate,
  onAccept,
  onCancel,
}: OptimizeDescriptionModalProps) {
  const { t } = useLingui();
  const [suggested, setSuggested] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Run the caller-supplied AI thunk and stash the suggestion. Surfaces
   * a copy-friendly error if the request fails so the user can retry
   * without leaving the modal.
   */
  async function handleGenerate() {
    setBusy(true);
    setError(null);
    try {
      const result = await generate();
      setSuggested(result);
    } catch {
      setError(t`Couldn't generate a suggestion. Try again in a moment.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <BaseModal
      open
      onOpenChange={(open) => !open && onCancel()}
      title={<Trans>Optimise template description</Trans>}
      description={<Trans>AI-suggest a clearer description for "{currentName}".</Trans>}
      className="sm:max-w-xl"
      footer={
        <>
          <Button variant="outline" onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={() => onAccept(suggested)} disabled={!suggested}>
            <Trans>Accept</Trans>
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="optimize-current-desc">
            <Trans>Current description</Trans>
          </Label>
          <Textarea
            id="optimize-current-desc"
            value={currentDescription}
            disabled
            readOnly
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="optimize-suggested-desc">
              <Trans>Suggested description</Trans>
            </Label>
            <Button variant="outline" size="sm" onClick={handleGenerate} disabled={busy}>
              {busy ? (
                <>
                  <Spinner className="mr-2 h-3 w-3" />
                  <Trans>Generating…</Trans>
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-3 w-3" />
                  <Trans>Generate</Trans>
                </>
              )}
            </Button>
          </div>
          <Textarea
            id="optimize-suggested-desc"
            value={suggested}
            onChange={(e) => setSuggested(e.target.value)}
            placeholder={t`Press Generate to get a suggestion`}
            rows={4}
          />
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
    </BaseModal>
  );
}

interface OptimizeDescriptionModalProps {
  currentName: string;
  currentDescription: string;
  generate: () => Promise<string>;
  onAccept: (description: string) => void;
  onCancel: () => void;
}
